import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { useEffect, useState } from 'react';

import { SelectDataFileActionSection, SelectDataFileInformationSection } from '@/components/SelectDataFileSections';
import { SensorCardSkeleton } from '@/components/SensorCard/SensorCardSkeleton';
import { SensorFlowModal } from '@/components/SensorFlowModal/SensorFlowModal';
import { StepperSkeleton } from '@/components/StepperSkeleton/StepperSkeleton';
import { toast } from '@/components/Toaster/Toast';

import { mergeClassNames } from '@/utils';

import { SensorEventMap } from '@/controllers/interfaces/IEventDispatcher';
import { hubSensorService, sensorService } from '@/controllers/sensorService';

import { useSessionScreenStore } from '@/hooks/useSessionScreenStore';

import { useSensorsContext } from '@/contexts/SensorsContext';

import Step1BackgroundImage from '@/assets/illustrations/upload-data-1.png';
import Step2BackgroundImage from '@/assets/illustrations/upload-data-2.png';

import styles from './HubConnectScreen.module.css';

interface HubConnectScreenProps {
  isHubFlow: boolean;
}

export const HubConnectScreen = ({ isHubFlow }: HubConnectScreenProps) => {
  const { goToScreen } = useSessionScreenStore();
  const [currentStep, setCurrentStep] = useState(0);
  const { setHubStatus } = useSensorsContext();

  // Switch to the non-hub flow when the user isn't using the hub (skipping this screen)
  useEffect(() => {
    if (!isHubFlow) {
      goToScreen(1);
    }
  }, [isHubFlow]);

  const onPlugInSensors = () => {
    setCurrentStep(1);
  };

  const onUploadFile = async () => {
    try {
      await hubSensorService.uploadFile(() => {
        hubSensorService.removeListener('onFileSelected--upload');
        setCurrentStep(2);
      });
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error uploading file',
        description: 'Could not write to the Hub. Please try again.',
      });
    }
  };

  const onUnplugSensors = () => {
    setCurrentStep(3);

    setTimeout(() => {
      onFinish();
    }, 3000);
  };

  const onFinish = () => {
    goToScreen(1);
  };

  /// There are no effects for unplugging the hub & plugging in the hub so we wait 3 seconds instead
  useEffect(() => {
    if (currentStep === 0) {
      setTimeout(() => {
        onPlugInSensors();
      }, 3000);
    }
  }, [currentStep]);

  useEffect(() => {
    async function connectToHub() {
      await hubSensorService.stopStreaming();
      setHubStatus('connecting');

      await hubSensorService.connectDevices(async () => {
        hubSensorService.removeListener('onHubConnected');
        onUnplugSensors();
        setHubStatus('connected');
      });
    }

    if (currentStep === 2) {
      connectToHub();
    }
  }, [currentStep]);

  return (
    <IonGrid className={mergeClassNames('ion-no-padding', styles.grid)} fixed>
      <IonRow className={styles.topArea}>
        <IonCol className={styles.left} size='4'>
          {[1, 2, 3, 4].map((index) => (
            <SensorCardSkeleton key={`${index}-left`} />
          ))}
        </IonCol>

        {currentStep === 0 && (
          <div className={styles.center}>
            <SensorFlowModal description='Plug *in* the hub' illustration='plugIn' />
          </div>
        )}

        {currentStep === 1 && (
          <div className={styles.fullScreen}>
            <SelectDataFileActionSection
              buttonOnclick={() => onUploadFile()}
              buttonText='Upload file'
              description='To connect with the sensor hub, tap *“Upload file”* and select the data file on your device.'
              title='Upload data file'
            />

            <SelectDataFileInformationSection
              steps={[
                {
                  index: 1,
                  image: Step1BackgroundImage,
                  title: 'Open the Sensor Hub location',
                  description: 'In your files select *QSENSEHUB* under the *locations* section on the left panel.',
                },
                {
                  index: 2,
                  image: Step2BackgroundImage,
                  title: 'Select the data file',
                  description:
                    'The right panel will show the files within, select *data* to allow connection with the hub.',
                },
              ]}
            />
          </div>
        )}

        {currentStep === 2 && (
          <div className={styles.center}>
            <SensorFlowModal
              description='Now *unplug* the Sensor Hub to allow for a Bluetooth connection'
              illustration='unplug'
            />
          </div>
        )}

        {currentStep === 3 && (
          <div className={styles.center}>
            <SensorFlowModal description='Sensor Hub connected successfully' illustration='success' />
          </div>
        )}
        <IonCol className={styles.right} size='4'>
          {[1, 2, 3, 4].map((index) => (
            <SensorCardSkeleton key={`${index}-right`} />
          ))}
        </IonCol>
      </IonRow>

      <IonRow className={styles.bottomArea}>
        <IonCol size='12'>
          <StepperSkeleton />
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};
